import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Text,
  Flex,
  TextField,
  TextArea,
  Heading,
  Separator,
} from "@radix-ui/themes";
import {
  EnvelopeClosedIcon,
  PersonIcon,
  MobileIcon,
  ClockIcon,
} from "@radix-ui/react-icons";
import HeadingBox from "../../components/common/HeadingBox";
import Button from "../../components/common/Button";
import hairCareImage from "../../assets/home/services/hair-care.jpg";
import styles from "./styles.module.scss";
import {
  animateContactHeader,
  animateContactForm,
  animateContactInfo,
  animateMapSection,
  refreshScrollTrigger,
} from "../../utils/animations";

const Contact = () => {
  const headerRef = useRef(null);
  const formRef = useRef(null);
  const infoRef = useRef(null);
  const mapRef = useRef(null);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Animation for Header
  useEffect(() => {
    return animateContactHeader(headerRef);
  }, []);

  // Animation for Form
  useEffect(() => {
    return animateContactForm(formRef);
  }, []);

  // Animation for Contact Info
  useEffect(() => {
    return animateContactInfo(infoRef);
  }, []);

  // Animation for Map Section
  useEffect(() => {
    return animateMapSection(mapRef);
  }, []);

  // Refresh ScrollTrigger after all animations are set up
  useEffect(() => {
    return refreshScrollTrigger();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      errors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (!formData.phone.trim()) {
      errors.phone = "Phone is required";
    } else if (!/^[\d\s\-+()]+$/.test(formData.phone)) {
      errors.phone = "Please enter a valid phone number";
    }

    if (!formData.subject.trim()) {
      errors.subject = "Subject is required";
    }

    if (!formData.message.trim()) {
      errors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      errors.message = "Message must be at least 10 characters";
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitSuccess(false);

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);

    // Simulate form submission (replace with actual API call)
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      console.log("Form submitted:", formData);
      setSubmitSuccess(true);
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });

      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <MobileIcon width="24" height="24" />,
      title: "Phone",
      content: "+961 81 964 456",
      description: "Call us anytime",
    },
    {
      icon: <EnvelopeClosedIcon width="24" height="24" />,
      title: "Email",
      content: "info@renova-aesthetic-clinic.com",
      description: "Send us an email",
    },
    {
      icon: <ClockIcon width="24" height="24" />,
      title: "Hours",
      content: "Mon - Sat: 9:00 AM - 7:00 PM",
      description: "Sunday: Closed",
    },
  ];

  return (
    <div style={{ background: "var(--color-secondary)", minHeight: "100vh" }}>
      {/* Header Section */}
      <Box ref={headerRef} className={styles.headerSection}>
        <Flex
          direction={{ initial: "column", md: "row" }}
          gap="6"
          align="center"
          className={styles.headerContent}
        >
          <Box className={styles.headerText}>
            <HeadingBox
              title="Get in Touch"
              description="We'd love to hear from you. Send us a message and we'll respond as soon as possible."
              textColor="var(--color-secondary)"
            />
          </Box>
          <Box
            className={styles.headerImageWrapper}
            onClick={() => {
              window.location.href = "/services";
            }}
            style={{ cursor: "pointer" }}
          >
            <img
              src={hairCareImage}
              alt="Book an Appointment"
              className={styles.headerImage}
            />
            <Box className={styles.headerImageOverlay}>
              <Button
                size="4"
                variant="normal"
                backgroundColor="var(--color-secondary)"
                textColor="var(--color-primary)"
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = "/services";
                }}
                className={styles.bookButton}
              >
                Explore Our Services
              </Button>
            </Box>
          </Box>
        </Flex>
      </Box>

      {/* Main Content Section */}
      <Box className={styles.mainContent}>
        {/* Contact Form Section */}
        <Box ref={formRef} className={styles.formSection}>
          <Box className={styles.formCard}>
            <Flex direction="column" gap="6">
              <Flex direction="column" gap="2">
                <Heading size="7" className={styles.formTitle}>
                  Send us a Message
                </Heading>
                <Text size="3" className={styles.formDescription}>
                  Fill out the form below and we'll get back to you within 24
                  hours.
                </Text>
              </Flex>

              {submitSuccess && (
                <Box className={styles.successMessage}>
                  <Text size="3" style={{ color: "var(--color-secondary)" }}>
                    Thank you! Your message has been sent successfully. We'll
                    get back to you soon.
                  </Text>
                </Box>
              )}

              <form onSubmit={handleSubmit} className={styles.contactForm}>
                <Flex direction="column" gap="4">
                  {/* First Row: First Name and Last Name */}
                  <Flex
                    direction={{ initial: "column", md: "row" }}
                    gap="4"
                    className={styles.formRow}
                  >
                    {/* First Name Field */}
                    <Box className={styles.formField}>
                      <Text
                        as="label"
                        size="2"
                        weight="medium"
                        className={styles.label}
                      >
                        First Name *
                      </Text>
                      <TextField.Root
                        size="3"
                        placeholder="Enter your first name"
                        value={formData.firstName}
                        onChange={(e) =>
                          handleInputChange("firstName", e.target.value)
                        }
                        className={styles.textField}
                        variant={formErrors.firstName ? "surface" : "soft"}
                        color={formErrors.firstName ? "red" : undefined}
                        style={{
                          backgroundColor: formErrors.firstName
                            ? "rgba(255, 0, 0, 0.1)"
                            : undefined,
                        }}
                      >
                        <TextField.Slot>
                          <PersonIcon />
                        </TextField.Slot>
                      </TextField.Root>
                      {formErrors.firstName && (
                        <Text size="1" color="red" className={styles.errorText}>
                          {formErrors.firstName}
                        </Text>
                      )}
                    </Box>

                    {/* Last Name Field */}
                    <Box className={styles.formField}>
                      <Text
                        as="label"
                        size="2"
                        weight="medium"
                        className={styles.label}
                      >
                        Last Name *
                      </Text>
                      <TextField.Root
                        size="3"
                        placeholder="Enter your last name"
                        value={formData.lastName}
                        onChange={(e) =>
                          handleInputChange("lastName", e.target.value)
                        }
                        className={styles.textField}
                        variant={formErrors.lastName ? "surface" : "soft"}
                        color={formErrors.lastName ? "red" : undefined}
                        style={{
                          backgroundColor: formErrors.lastName
                            ? "rgba(255, 0, 0, 0.1)"
                            : undefined,
                        }}
                      >
                        <TextField.Slot>
                          <PersonIcon />
                        </TextField.Slot>
                      </TextField.Root>
                      {formErrors.lastName && (
                        <Text size="1" color="red" className={styles.errorText}>
                          {formErrors.lastName}
                        </Text>
                      )}
                    </Box>
                  </Flex>

                  {/* Second Row: Email and Phone Number */}
                  <Flex
                    direction={{ initial: "column", md: "row" }}
                    gap="4"
                    className={styles.formRow}
                  >
                    {/* Email Field */}
                    <Box className={styles.formField}>
                      <Text
                        as="label"
                        size="2"
                        weight="medium"
                        className={styles.label}
                      >
                        Email Address *
                      </Text>
                      <TextField.Root
                        size="3"
                        type="email"
                        placeholder="Enter your email address"
                        value={formData.email}
                        onChange={(e) =>
                          handleInputChange("email", e.target.value)
                        }
                        className={styles.textField}
                        variant={formErrors.email ? "surface" : "soft"}
                        color={formErrors.email ? "red" : undefined}
                        style={{
                          backgroundColor: formErrors.email
                            ? "rgba(255, 0, 0, 0.1)"
                            : undefined,
                        }}
                      >
                        <TextField.Slot>
                          <EnvelopeClosedIcon />
                        </TextField.Slot>
                      </TextField.Root>
                      {formErrors.email && (
                        <Text size="1" color="red" className={styles.errorText}>
                          {formErrors.email}
                        </Text>
                      )}
                    </Box>

                    {/* Phone Field */}
                    <Box className={styles.formField}>
                      <Text
                        as="label"
                        size="2"
                        weight="medium"
                        className={styles.label}
                      >
                        Phone Number *
                      </Text>
                      <TextField.Root
                        size="3"
                        type="tel"
                        placeholder="Enter your phone number"
                        value={formData.phone}
                        onChange={(e) =>
                          handleInputChange("phone", e.target.value)
                        }
                        className={styles.textField}
                        variant={formErrors.phone ? "surface" : "soft"}
                        color={formErrors.phone ? "red" : undefined}
                        style={{
                          backgroundColor: formErrors.phone
                            ? "rgba(255, 0, 0, 0.1)"
                            : undefined,
                        }}
                      >
                        <TextField.Slot>
                          <MobileIcon />
                        </TextField.Slot>
                      </TextField.Root>
                      {formErrors.phone && (
                        <Text size="1" color="red" className={styles.errorText}>
                          {formErrors.phone}
                        </Text>
                      )}
                    </Box>
                  </Flex>

                  {/* Third Row: Subject (Alone) */}
                  <Box>
                    <Text
                      as="label"
                      size="2"
                      weight="medium"
                      className={styles.label}
                    >
                      Subject *
                    </Text>
                    <TextField.Root
                      size="3"
                      placeholder="What is this regarding?"
                      value={formData.subject}
                      onChange={(e) =>
                        handleInputChange("subject", e.target.value)
                      }
                      className={styles.textField}
                      variant={formErrors.subject ? "surface" : "soft"}
                      color={formErrors.subject ? "red" : undefined}
                      style={{
                        backgroundColor: formErrors.subject
                          ? "rgba(255, 0, 0, 0.1)"
                          : undefined,
                      }}
                    />
                    {formErrors.subject && (
                      <Text size="1" color="red" className={styles.errorText}>
                        {formErrors.subject}
                      </Text>
                    )}
                  </Box>

                  {/* Message Field - Full Width */}
                  <Box>
                    <Text
                      as="label"
                      size="2"
                      weight="medium"
                      className={styles.label}
                    >
                      Message *
                    </Text>
                    <TextArea
                      size="3"
                      placeholder="Tell us more about your inquiry..."
                      value={formData.message}
                      onChange={(e) =>
                        handleInputChange("message", e.target.value)
                      }
                      className={styles.textArea}
                      rows={6}
                      variant={formErrors.message ? "surface" : "soft"}
                      color={formErrors.message ? "red" : undefined}
                      style={{
                        backgroundColor: formErrors.message
                          ? "rgba(255, 0, 0, 0.1)"
                          : undefined,
                      }}
                    />
                    {formErrors.message && (
                      <Text size="1" color="red" className={styles.errorText}>
                        {formErrors.message}
                      </Text>
                    )}
                  </Box>

                  {/* Submit Button */}
                  <Flex justify="end" className={styles.submitButtonContainer}>
                    <Button
                      type="submit"
                      size="4"
                      variant="outlined"
                      backgroundColor="var(--color-primary)"
                      textColor="var(--color-secondary)"
                      disabled={isSubmitting}
                      className={styles.submitButton}
                    >
                      {isSubmitting ? "Sending..." : "Send Message"}
                    </Button>
                  </Flex>
                </Flex>
              </form>
            </Flex>
          </Box>
        </Box>

        {/* Contact Info Section */}
        <Box ref={infoRef} className={styles.infoSection}>
          <Box className={styles.infoCard}>
            <Flex direction="column" gap="6">
              <Flex direction="column" gap="2">
                <Heading size="7" className={styles.infoTitle}>
                  Contact Information
                </Heading>
                <Text size="3" className={styles.infoDescription}>
                  Reach out to us through any of these channels
                </Text>
              </Flex>

              <Separator size="4" />

              <Flex direction="column" gap="5" className={styles.infoItems}>
                {contactInfo.map((info, index) => (
                  <Box key={index} className={styles.infoItem}>
                    <Flex gap="4" align="start">
                      <Box className={styles.infoIconWrapper}>{info.icon}</Box>
                      <Flex
                        direction="column"
                        gap="1"
                        className={styles.infoContent}
                      >
                        <Text
                          size="4"
                          weight="bold"
                          className={styles.infoItemTitle}
                        >
                          {info.title}
                        </Text>
                        <Text size="3" className={styles.infoItemContent}>
                          {info.content}
                        </Text>
                        <Text size="2" className={styles.infoItemDescription}>
                          {info.description}
                        </Text>
                      </Flex>
                    </Flex>
                  </Box>
                ))}
              </Flex>

              <Separator size="4" />
            </Flex>
          </Box>
        </Box>
      </Box>

      {/* Map Section */}
      <Box ref={mapRef} className={styles.mapSection}>
        <Box className={styles.mapCard}>
          <Flex direction="column" gap="4">
            <Flex direction="column" gap="2">
              <Heading size="6" className={styles.mapTitle}>
                Find Us
              </Heading>
              <Text size="3" className={styles.mapDescription}>
                Visit us at our clinic in Zahle, Lebanon
              </Text>
            </Flex>
            <Box className={styles.mapContainer}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3312.3187368677846!2d35.90740231527804!3d33.84936898065478!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x151f58c5e0a5a5a5%3A0x5a5a5a5a5a5a5a5a!2sZahle%2C%20Lebanon!5e0!3m2!1sen!2slb!4v1234567890123!5m2!1sen!2slb"
                width="100%"
                height="400"
                style={{ border: 0, borderRadius: "16px" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Renova Aesthetic Clinic Location"
              />
            </Box>
          </Flex>
        </Box>
      </Box>
    </div>
  );
};

export default Contact;
